// Error envelope { code, message, details?, request_id } shared by PostgREST, the Edge
// Functions and Storage (services-api). Every exported function of this package throws
// an ErreurHorizon and nothing else, so the interface only has one failure shape to render.

import { codeErreur, enveloppeErreur } from '@horizon/types';
import type { CodeErreur, EnveloppeErreur } from '@horizon/types';
import { codeDepuisPostgres, codeDepuisStatut, messageRegle } from './codes-postgres.js';

/** User facing wording of each error code (services-api reference table). */
export const MESSAGES_UTILISATEUR: Readonly<Record<CodeErreur, string>> = {
  SAISIE_INVALIDE: 'Le formulaire contient des erreurs : vérifiez les champs signalés.',
  NON_AUTHENTIFIE: 'Votre session a expiré : reconnectez-vous.',
  ACCES_REFUSE: 'Accès non autorisé.',
  INTROUVABLE: 'Élément introuvable.',
  CONFLIT: "L'élément a changé : actualisez la page.",
  // REGLE_VIOLEE carries a rule specific message; this is the fallback wording.
  REGLE_VIOLEE: "L'opération est refusée par une règle de gestion.",
  ERREUR_INTERNE: "Une erreur est survenue : réessayez, l'incident est enregistré.",
};

export interface OptionsErreurHorizon {
  readonly code: CodeErreur;
  /** Technical message, kept for the logs and Sentry. Never displayed as is. */
  readonly message: string;
  readonly messageUtilisateur?: string | undefined;
  readonly details?: unknown;
  readonly requestId?: string | undefined;
  /** True when the failure comes from the transport and can be retried. */
  readonly reseau?: boolean | undefined;
  readonly cause?: unknown;
}

/** Correlation identifier used when the server did not provide one (RNF-27). */
function nouvelIdentifiantRequete(): string {
  const source = globalThis.crypto;
  if (source !== undefined && typeof source.randomUUID === 'function') {
    return source.randomUUID();
  }
  return `loc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Normalised failure of the data access layer. */
export class ErreurHorizon extends Error {
  readonly code: CodeErreur;
  readonly messageUtilisateur: string;
  readonly details: unknown;
  readonly requestId: string;
  readonly reseau: boolean;

  constructor(options: OptionsErreurHorizon) {
    super(options.message, { cause: options.cause });
    this.name = 'ErreurHorizon';
    this.code = options.code;
    this.messageUtilisateur = options.messageUtilisateur ?? MESSAGES_UTILISATEUR[options.code];
    this.details = options.details;
    this.requestId = options.requestId ?? nouvelIdentifiantRequete();
    this.reseau = options.reseau ?? false;
  }

  /** Serialisable envelope, suitable for the logs and the support form. */
  enveloppe(): EnveloppeErreur {
    return {
      code: this.code,
      message: this.message,
      details: this.details,
      request_id: this.requestId,
    };
  }
}

function estObjet(valeur: unknown): valeur is Record<string, unknown> {
  return typeof valeur === 'object' && valeur !== null;
}

function texte(valeur: unknown): string | undefined {
  return typeof valeur === 'string' && valeur.length > 0 ? valeur : undefined;
}

function nombre(valeur: unknown): number | undefined {
  return typeof valeur === 'number' && Number.isFinite(valeur) ? valeur : undefined;
}

/** True for a transport failure: offline, DNS, TLS, timeout or aborted request. */
export function estErreurReseau(valeur: unknown): boolean {
  if (valeur instanceof ErreurHorizon) {
    return valeur.reseau;
  }
  if (!(valeur instanceof Error)) {
    return false;
  }
  if (valeur.name === 'AbortError' || valeur.name === 'TimeoutError') {
    return true;
  }
  const message = valeur.message.toLowerCase();
  return (
    valeur instanceof TypeError &&
    (message.includes('fetch') || message.includes('network') || message.includes('load failed'))
  );
}

function depuisEnveloppe(valeur: Record<string, unknown>): ErreurHorizon | null {
  const analyse = enveloppeErreur.safeParse(valeur);
  if (!analyse.success) {
    return null;
  }
  const envelope = analyse.data;
  return new ErreurHorizon({
    code: envelope.code,
    message: envelope.message,
    messageUtilisateur:
      envelope.code === 'REGLE_VIOLEE' ? messageRegle(envelope.message) : undefined,
    details: envelope.details,
    requestId: envelope.request_id,
  });
}

function depuisPostgrest(valeur: Record<string, unknown>): ErreurHorizon | null {
  const code = texte(valeur['code']);
  const message = texte(valeur['message']);
  if (message === undefined || !('details' in valeur && 'hint' in valeur)) {
    return null;
  }
  const codeEnveloppe = codeDepuisPostgres(code);
  return new ErreurHorizon({
    code: codeEnveloppe,
    message: `${code ?? 'sans_code'}: ${message}`,
    messageUtilisateur: codeEnveloppe === 'REGLE_VIOLEE' ? messageRegle(message) : undefined,
    details: { details: valeur['details'], hint: valeur['hint'] },
    cause: valeur,
  });
}

function depuisStatut(valeur: Record<string, unknown>): ErreurHorizon | null {
  const statut = nombre(valeur['status']);
  const estAuth = valeur['__isAuthError'] === true;
  const nom = texte(valeur['name']);
  const estStorage = nom === 'StorageApiError' || nom === 'StorageUnknownError';
  if (statut === undefined && !estAuth && !estStorage) {
    return null;
  }
  const message = texte(valeur['message']) ?? 'requête refusée';
  return new ErreurHorizon({
    code: estAuth && statut === undefined ? 'NON_AUTHENTIFIE' : codeDepuisStatut(statut),
    message,
    cause: valeur,
  });
}

/**
 * Normalises any thrown value into an ErreurHorizon. Recognises, in order, an already
 * normalised error, a server envelope, a PostgrestError, an AuthError or a StorageError,
 * then a transport failure, and finally falls back to ERREUR_INTERNE.
 */
export function toErreur(valeur: unknown, requestId?: string): ErreurHorizon {
  if (valeur instanceof ErreurHorizon) {
    return valeur;
  }
  if (estObjet(valeur)) {
    const candidat = depuisEnveloppe(valeur) ?? depuisPostgrest(valeur) ?? depuisStatut(valeur);
    if (candidat !== null) {
      return candidat;
    }
  }
  if (estErreurReseau(valeur)) {
    return new ErreurHorizon({
      code: 'ERREUR_INTERNE',
      message: valeur instanceof Error ? valeur.message : 'échec réseau',
      messageUtilisateur: 'Connexion interrompue : vérifiez votre réseau puis réessayez.',
      reseau: true,
      requestId,
      cause: valeur,
    });
  }
  return new ErreurHorizon({
    code: 'ERREUR_INTERNE',
    message: valeur instanceof Error ? valeur.message : String(valeur),
    requestId,
    cause: valeur,
  });
}

/** Builds a local error without any server round trip (client side validation). */
export function erreurLocale(code: CodeErreur, message: string, details?: unknown): ErreurHorizon {
  const options: OptionsErreurHorizon = { code, message, details };
  return new ErreurHorizon(
    code === 'REGLE_VIOLEE' ? { ...options, messageUtilisateur: messageRegle(message) } : options,
  );
}

export interface OptionsReprise {
  /** Total number of attempts, the first one included. */
  readonly tentatives?: number;
  readonly delaiInitialMs?: number;
  readonly facteur?: number;
  /** Injectable so the tests stay deterministic and instantaneous. */
  readonly pause?: (ms: number) => Promise<void>;
  readonly alea?: () => number;
}

const TENTATIVES_DEFAUT = 3;
const DELAI_INITIAL_MS = 300;
const FACTEUR_DEFAUT = 2;

function attendre(ms: number): Promise<void> {
  return new Promise((resoudre) => {
    setTimeout(resoudre, ms);
  });
}

/**
 * Runs an operation and retries it on transport failures only, with an exponential
 * backoff and a plus or minus 20 percent jitter. Business errors are never retried:
 * replaying a 409 or a 422 would only duplicate the failure.
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options: OptionsReprise = {},
): Promise<T> {
  const tentatives = Math.max(1, options.tentatives ?? TENTATIVES_DEFAUT);
  const delaiInitial = options.delaiInitialMs ?? DELAI_INITIAL_MS;
  const facteur = options.facteur ?? FACTEUR_DEFAUT;
  const pause = options.pause ?? attendre;
  const alea = options.alea ?? Math.random;

  let derniere: ErreurHorizon = erreurLocale('ERREUR_INTERNE', 'aucune tentative exécutée');
  for (let essai = 1; essai <= tentatives; essai += 1) {
    try {
      return await operation();
    } catch (echec) {
      derniere = toErreur(echec);
      if (!derniere.reseau || essai === tentatives) {
        throw derniere;
      }
      const base = delaiInitial * Math.pow(facteur, essai - 1);
      await pause(Math.round(base * (0.8 + alea() * 0.4)));
    }
  }
  throw derniere;
}

/** Type guard usable from the interface layer. */
export function estErreurHorizon(valeur: unknown): valeur is ErreurHorizon {
  return valeur instanceof ErreurHorizon;
}

export { codeErreur };
export type { CodeErreur, EnveloppeErreur };
